import type { RedirectLoginOptions } from '@auth0/auth0-react'
import { setAccessTokenGetter, getErrorStatus } from '@doneverse/data/api'
import { userProfileOptions } from '@doneverse/data/queries'
import { profileService } from '@doneverse/data/services'
import type { BusinessBriefData } from '@doneverse/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useState } from 'react'
import { AuthContext, type AuthContextType, type AuthUser, type Profile } from './AuthContext'
import { bootstrapWorkspaceSelection } from '@/lib/workspace-bootstrap'
import { useDashboardStore } from '@/store'

const E2E_USER: AuthUser = {
  sub: 'auth0|test-user-123',
  email: 'e2e@doneverse.com',
  name: 'E2E Test User',
  picture: '',
  email_verified: true,
  updated_at: new Date().toISOString(),
  id: 'auth0|test-user-123',
}

const E2E_AUTH_OVERRIDE_KEYS = {
  bootTimeoutMs: 'doneverse.e2e.auth_boot_timeout_ms',
  forceLoading: 'doneverse.e2e.auth_force_loading',
  forceUserMissing: 'doneverse.e2e.auth_force_user_missing',
  authErrorStatus: 'doneverse.e2e.auth_error_status',
  authErrorMessage: 'doneverse.e2e.auth_error_message',
} as const

function readBooleanFlag(key: string): boolean {
  return localStorage.getItem(key) === 'true'
}

function readNumberFlag(key: string): number | null {
  const raw = localStorage.getItem(key)
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

function clearAuthOverrides() {
  for (const key of Object.values(E2E_AUTH_OVERRIDE_KEYS)) {
    localStorage.removeItem(key)
  }
}

function setE2ESession(user: unknown) {
  localStorage.setItem('token', 'mock-test-token')
  localStorage.setItem('e2e_test_user', JSON.stringify(user))
}

function clearE2ESession() {
  localStorage.removeItem('token')
  localStorage.removeItem('e2e_test_user')
}

export function E2EAuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [hasTestUser, setHasTestUser] = useState(() => !!localStorage.getItem('e2e_test_user'))
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const forceLoading = readBooleanFlag(E2E_AUTH_OVERRIDE_KEYS.forceLoading)
  const forceUserMissing = readBooleanFlag(E2E_AUTH_OVERRIDE_KEYS.forceUserMissing)
  const forcedAuthErrorStatus = readNumberFlag(E2E_AUTH_OVERRIDE_KEYS.authErrorStatus)
  const forcedAuthErrorMessage = localStorage.getItem(E2E_AUTH_OVERRIDE_KEYS.authErrorMessage)

  const user = hasTestUser && !forceUserMissing ? E2E_USER : undefined
  const isAuthenticated = hasTestUser

  const getAccessToken = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated')
    return localStorage.getItem('token') || 'mock-test-token'
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      setAccessTokenGetter(getAccessToken)
    } else {
      setAccessTokenGetter(null)
    }
    return () => {
      setAccessTokenGetter(null)
    }
  }, [getAccessToken, isAuthenticated])

  const {
    data: rawProfile = null,
    isLoading: isProfileLoading,
    error: profileError,
  } = useQuery({
    ...userProfileOptions(user?.id ?? null),
    queryFn: async () => {
      await getAccessToken()
      const fetched = await profileService.fetchProfile()
      // Same boot-order guarantee as the real AuthContext: active workspace is
      // set before the router (and its prefetches) can mount.
      bootstrapWorkspaceSelection(queryClient, fetched.id, fetched.workspaces)
      return fetched
    },
    enabled: !!(isAuthenticated && user),
  })

  const loginWithPassword = useCallback(async (email: string, _password: string) => {
    const trimmedEmail = email.trim().toLowerCase()
    if (!trimmedEmail || !_password) {
      return { error: new Error('Email and password are required') }
    }
    const e2eTestUser = {
      auth0_id: 'auth0|founder-e2e-1',
      email: trimmedEmail,
      name: 'E2E Founder User',
      role: 'founder' as const,
    }
    setE2ESession(e2eTestUser)
    setHasTestUser(true)
    return { error: null }
  }, [])

  const signupWithPassword = useCallback(
    async (email: string, password: string) => {
      if (password.length < 8) {
        return { error: new Error('Password must be at least 8 characters long') }
      }
      return await loginWithPassword(email, password)
    },
    [loginWithPassword],
  )

  const logout = async (options?: { returnTo?: string }) => {
    const returnTo = options?.returnTo
      ? `${window.location.origin}${options.returnTo.startsWith('/') ? options.returnTo : ''}`
      : window.location.origin
    setIsLoggingOut(true)
    clearAuthOverrides()
    clearE2ESession()
    setHasTestUser(false)
    queryClient.clear()
    window.location.href = returnTo
  }

  const loginWithRedirect = async (options?: RedirectLoginOptions) => {
    const e2eTestUser = {
      auth0_id: 'auth0|founder-e2e-1',
      email: 'founder-e2e@doneverse.com',
      name: 'E2E Founder User',
      role: 'founder' as const,
    }
    setE2ESession(e2eTestUser)
    setHasTestUser(true)
    window.location.href = options?.authorizationParams?.redirect_uri || '/doing-blueprint'
  }

  const updateProfile = async (
    updates: Partial<Omit<Profile, 'business_brief'>> & {
      business_brief?: BusinessBriefData
    },
  ) => {
    if (!isAuthenticated) return { error: new Error('Not authenticated') }
    try {
      if (updates.business_brief !== undefined) {
        await profileService.updateBusinessBrief(updates.business_brief)
      }
      queryClient.setQueryData(userProfileOptions(user?.id ?? null).queryKey, (prev: Profile | undefined) => {
        if (!prev) return prev
        const next = { ...prev, ...updates } as Profile
        if (updates.business_brief !== undefined) {
          next.business_brief = updates.business_brief ?? null
        }
        return next
      })
      return { error: null }
    } catch (error) {
      console.error('Error updating profile:', error)
      return {
        error: error instanceof Error ? error : new Error('Failed to update profile'),
      }
    }
  }

  const profile = isAuthenticated ? rawProfile : null
  const activeUserId = profile?.id ?? null

  useEffect(() => {
    useDashboardStore.getState().setCurrentUserId(activeUserId)
  }, [activeUserId])

  const userWithId: AuthUser | undefined = user
    ? { ...user, id: profile?.id ?? user.id ?? user.sub ?? '' }
    : undefined
  const resolvedProfile = forcedAuthErrorStatus ? null : profile
  const isLoadingCombined = forceLoading || isProfileLoading
  const authError =
    forcedAuthErrorMessage ??
    (isAuthenticated && profileError
      ? profileError instanceof Error
        ? profileError.message
        : 'Failed to load profile'
      : null)
  const authErrorStatus = forcedAuthErrorStatus ?? (isAuthenticated ? getErrorStatus(profileError) : null)

  const value: AuthContextType = {
    user: userWithId,
    isAuthenticated,
    isLoading: isLoadingCombined,
    loading: isLoadingCombined,
    isLoggingOut,
    profile: resolvedProfile,
    authError,
    authErrorStatus,
    getAccessToken,
    getRealtimeJwt: getAccessToken,
    loginWithPassword,
    signupWithPassword,
    loginWithRedirect,
    logout,
    updateProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
